import PropTypes from 'prop-types';
import { css } from '@emotion/react';

/**
 * Flex element for not writing any more custom flex styles to quickly managing
 * the layout.
 */
const flexboxCss = props => css`
  align-content: ${props.alignContent};
  align-self: ${props.alignSelf};
  align-items: ${props.alignItems};
  display: ${props.inline ? 'inline-flex' : 'flex'};
  flex: ${props.flex};
  flex-basis: ${props.basis};
  flex-direction: ${props.direction};
  flex-grow: ${props.grow};
  flex-shrink: ${props.shrink};
  flex-wrap: ${props.wrap};
  justify-content: ${props.justifyContent};
  order: ${props.order};
`;

/**
 * @param {object} props
 */
function Flexbox({
  children,
  className,
  component: Component = 'div',
  ...restProps
}) {
  return (
    <Component css={flexboxCss(restProps)} className={className}>
      {children}
    </Component>
  );
}

Flexbox.propTypes = {
  className: PropTypes.string,
  /** The content of the component. */
  children: PropTypes.node,
  /**
   * The component used for the root node.
   * Either a string to use a DOM element or a React component.
   */
  component: PropTypes.elementType,
  /** Defines the `justify-content` style property. */
  justifyContent: PropTypes.oneOf([
    'center',
    'flex-end',
    'flex-start',
    'space-around',
    'space-between',
  ]),
  /** Defines the `align-items` style property. */
  alignItems: PropTypes.oneOf([
    'baseline',
    'center',
    'flex-end',
    'flex-start',
    'stretch',
  ]),
  /** Defines the `align-content` style property. */
  alignContent: PropTypes.oneOf([
    'center',
    'flex-end',
    'flex-start',
    'space-around',
    'space-between',
    'stretch',
  ]),
  /**
   * Defines the `align-self` style property.
   * Useful on a child of a Flexbox component.
   */
  alignSelf: PropTypes.oneOf([
    'baseline',
    'center',
    'flex-end',
    'flex-start',
    'stretch',
  ]),
  /** Defines the `flex-direction` style property. */
  direction: PropTypes.oneOf([
    'column-reverse',
    'column',
    'row-reverse',
    'row',
  ]),
  /** Defines the `flex` style property. */
  flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Defines the `flex-basis` style property. */
  basis: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Defines the `flex-grow` style property. */
  grow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Defines the `flex-shrink` style property. */
  shrink: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Defines the `flex-wrap` style property. */
  wrap: PropTypes.oneOf(['nowrap', 'wrap-reverse', 'wrap']),
  /** Defines the `order` style property. */
  order: PropTypes.number,
  /** Determines if the component should be an `inline-flex` element. */
  inline: PropTypes.bool,
};

export default Flexbox;
